import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { SalesOrderWorkspaceLayout } from '../components/workspace/SalesOrderWorkspaceLayout';
import { SalesOrderListFilters } from '../components/workspace/SalesOrderListFilters';
import { SalesOrderMasterList } from '../components/workspace/SalesOrderMasterList';
import { SalesOrderPinnedStrip } from '../components/workspace/SalesOrderPinnedStrip';
import { SalesOrderDetailPanel } from '../components/workspace/SalesOrderDetailPanel';
import {
  type FilterTab,
  useSalesOrderWorkspaceStore,
} from '../store/salesOrderWorkspaceStore';
import { useSalesOrderStore } from '../store/salesOrderStore';
import { useSalesOrderEventStream } from '../hooks/useSalesOrderEventStream';
import { usePostingSurfacePresence } from '@/features/notifications/hooks/usePostingSurfacePresence';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { PERMISSIONS } from '@features/shared/constants/permissions';

const VALID_FILTERS: FilterTab[] = ['drafts', 'submitted', 'archived'];

// FO-SOI-4 master-detail Sales Order workspace.
//
// URL is the source of truth for `selectedId` and `statusFilter`. The store
// holds session-persisted UI state (master pane width, last-used filter,
// search input). On mount we hydrate the URL into the store; on store change
// we push back to the URL with `replace: true` so the back button doesn't
// fill with every row click. Search isn't URL-driven — typing should be
// cheap, no history entries.

export function SalesOrderWorkspacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreate = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.SALES_ORDERS_CREATE_SALES_ORDER),
  );
  const canSubmit = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.SALES_ORDERS_SUBMIT_SALES_ORDER),
  );

  const selectedId = useSalesOrderWorkspaceStore((s) => s.selectedId);
  // N8 — while a sales order is selected and this tab is focused, its own posting outcome does not
  // also toast: the card in the panel has already said it.
  usePostingSurfacePresence('posting.so', selectedId ?? null);
  const setSelectedId = useSalesOrderWorkspaceStore((s) => s.setSelectedId);
  const statusFilter = useSalesOrderWorkspaceStore((s) => s.statusFilter);
  const setStatusFilter = useSalesOrderWorkspaceStore((s) => s.setStatusFilter);
  const quickFilter = useSalesOrderWorkspaceStore((s) => s.quickFilter);
  const setQuickFilter = useSalesOrderWorkspaceStore((s) => s.setQuickFilter);

  const startNewDraft = useSalesOrderStore((s) => s.startNewDraft);

  /* M4 (86d44z223) — Recent. Same guard as quotations: the label rides ONLY when the loaded draft
     is the one selected. `activeDraft` still holds the previous sales order while the next one
     loads, and a stale label under a fresh id is a silently wrong Recent row. */
  const activeDraft = useSalesOrderStore((s) => s.activeDraft);
  useRecordVisit(
    'sales-order',
    selectedId,
    activeDraft?.id === selectedId ? activeDraft?.salesOrderNumber : null
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastUrlSyncRef = useRef<{ id: string | null; filter: string | null }>({
    id: null,
    filter: null,
  });

  // URL → store hydration on mount + deep-link paste.
  useEffect(() => {
    const idParam = searchParams.get('id');
    const filterParam = searchParams.get('filter');

    if (
      lastUrlSyncRef.current.id !== idParam ||
      lastUrlSyncRef.current.filter !== filterParam
    ) {
      const numericId = idParam ? Number(idParam) : null;
      if (numericId && Number.isFinite(numericId) && numericId > 0) {
        setSelectedId(numericId);
      } else if (idParam === null) {
        setSelectedId(null);
      }
      if (filterParam && VALID_FILTERS.includes(filterParam as FilterTab)) {
        setStatusFilter(filterParam as FilterTab);
      }
      lastUrlSyncRef.current = { id: idParam, filter: filterParam };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Store → URL sync. `drafts` is the default — omit from the URL when set.
  useEffect(() => {
    const currentId = searchParams.get('id');
    const currentFilter = searchParams.get('filter');
    const desiredId = selectedId ? String(selectedId) : null;
    const desiredFilter = statusFilter === 'drafts' ? null : statusFilter;

    if (currentId === desiredId && currentFilter === desiredFilter) return;

    const next = new URLSearchParams(searchParams);
    if (desiredId) next.set('id', desiredId);
    else next.delete('id');
    if (desiredFilter) next.set('filter', desiredFilter);
    else next.delete('filter');

    lastUrlSyncRef.current = { id: desiredId, filter: desiredFilter };
    setSearchParams(next, { replace: true });
  }, [selectedId, statusFilter, searchParams, setSearchParams]);

  // `/` focuses search regardless of which pane is active.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSelect = useCallback(
    (id: number) => {
      setSelectedId(id);
    },
    [setSelectedId],
  );

  // New drafts compose inline. startNewDraft seeds the store with a blank
  // SalesOrderDto (id=0); clearing selectedId tells the detail panel to
  // render the unsaved local-only draft instead of loading by id. The first
  // save assigns a serverId; handleAfterSave then promotes selectedId so the
  // URL syncs and the master list picks up the new row.
  // Bumped on each "New" so the detail panel pops the FO picker modal — creation
  // starts by choosing a Firm Offer (the seed) rather than an inline picker.
  const [createNonce, setCreateNonce] = useState(0);
  const handleCreate = useCallback(() => {
    startNewDraft();
    setSelectedId(null);
    setCreateNonce((n) => n + 1);
  }, [startNewDraft, setSelectedId]);

  // M3 (86d44z1xm) — the shell's "New sales order" quick action. This page had `id` and `filter`
  // markers but no `?new`, so the shortcut had nowhere to land: a bare /sales-orders/workspace
  // creates nothing. Mirrors `?new=1` at QuotationWorkspacePage.tsx:186.
  //
  // It must go through `handleCreate` rather than calling `startNewDraft` directly — creating an SO
  // also bumps `createNonce`, which is what pops the FO picker. Skipping that leaves the user on an
  // empty draft with no forward order and no prompt.
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    handleCreate();
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, handleCreate, setSearchParams]);

  // After a save we only promote the selection (a new draft's id → URL/selection).
  // The master list refreshes itself off the SSE `Updated` event the save emits —
  // a surgical row splice — so we no longer trigger a full list refetch here, which
  // used to flicker every row and bounce the edited draft to the top on each
  // autosave (86d3acbub).
  const handleAfterSave = useCallback(
    (savedServerId: number) => {
      if (savedServerId !== selectedId) setSelectedId(savedServerId);
    },
    [selectedId, setSelectedId],
  );

  // SSE — single connection per page; the master list subscribes via the
  // bus. We don't surface connectionState in the filter bar (the prior pill
  // crowded the search input — matches what the quotation team chose).
  useSalesOrderEventStream();
  // ⛔ N8 (86d3w2kez): `useSalesOrderPostingToasts` WAS MOUNTED HERE AND IS DELETED. The notification
  // centre now raises exactly ONE outcome toast per posting, keyed on the job. Left in place, this
  // hook would fire alongside it and every sales-order post would double-toast. Its "Open" action is
  // replaced by the toast's own Open, which deep-links to the record with the arrival flash.

  return (
    // -m-6 negates AppLayout's p-6 so the workspace fills edge-to-edge.
    // h-screen fills the full height now that the global top bar is gone.
    // Same pattern the quotation workspace uses.
    <div className="-m-6 flex h-screen flex-col overflow-hidden">
      <SalesOrderWorkspaceLayout
        master={
          <>
            <SalesOrderListFilters
              ref={searchInputRef}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              quickFilter={quickFilter}
              onQuickFilterChange={setQuickFilter}
            />
            <SalesOrderMasterList
              statusFilter={statusFilter}
              quickFilter={quickFilter}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
            {canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                className="absolute bottom-4 right-4 z-10 inline-flex h-10 items-center gap-1.5 rounded-full bg-primary pl-3 pr-4 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="New sales order"
                title="New sales order"
              >
                <Plus className="h-4 w-4" />
                New
              </button>
            )}
          </>
        }
        detail={
          <div className="flex h-full min-h-0 flex-col">
            <SalesOrderPinnedStrip selectedId={selectedId} onSelect={handleSelect} />
            <div className="min-h-0 flex-1">
              <SalesOrderDetailPanel
                selectedId={selectedId}
                canCreate={canCreate}
                canSubmit={canSubmit}
                onCreate={handleCreate}
                onAfterSave={handleAfterSave}
                openFoPickerNonce={createNonce}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
